import { defineStore } from 'pinia'
import { useInstanceStore } from '@/store/backendInstances'
import backendHandler from '@/BackendAPI'
import CamerasSyncable from '@/syncing/syncables/CamerasSyncable.js'
import { useSettingsStore } from '@/store/settings.ts'
import SyncableHandler from '@/syncing/SyncableHandler.js'
import { useLogsStore } from '@/store/logs'

export const useCamerasStore = defineStore('cameras', {
  state: () => ({
    cameras: {},
    currentStillFrames: {},
    blockedThumbnailLoadings: {},
    fallbackFrames: {},
    camerasLoadingHandles: {},
  }),
  persist: false,
  share: {
    enable: true,
    omit: ['fallbackFrames', 'blockedThumbnailLoadings', 'currentStillFrames'],
    initialize: true,
  },
  getters: {
    getCameras(state) {
      return (globalUniqueIdentifier = useInstanceStore().selectedInstanceId) => state.cameras[globalUniqueIdentifier]
    },
    getNonDecommissionedCameras() {
      return (globalUniqueIdentifier = useInstanceStore().selectedInstanceId) => {
        const cameras = this.getCameras(globalUniqueIdentifier)
        if (!cameras) return []
        return cameras.filter(camera => !camera.tags.includes('decommissioned'))
      }
    },
    getCameraById() {
      return (globalUniqueIdentifier = useInstanceStore().selectedInstanceId, cameraId) => {
        const cameras = this.getCameras(globalUniqueIdentifier)
        if (!cameras) return null
        return cameras.find(camera => camera.cameraId === cameraId)
      }
    },
  },
  actions: {
    async loadCurrentStillFrame(
      cameraId,
      { resizeY = 1080, draw = undefined, text = undefined, forceFallback = undefined, priority = 5, signal = undefined } = {}
    ) {
      if (this.blockedThumbnailLoadings[cameraId] && this.blockedThumbnailLoadings[cameraId][resizeY]) return
      if (!this.blockedThumbnailLoadings[cameraId]) this.blockedThumbnailLoadings[cameraId] = {}
      this.blockedThumbnailLoadings[cameraId][resizeY] = true
      const params = {}
      if (resizeY !== undefined) params.resizeY = resizeY
      if (draw !== undefined) params.draw = draw
      if (text !== undefined) params.text = text
      if (forceFallback !== undefined) params.forceFallback = forceFallback
      const frameResponse = await backendHandler.get(useInstanceStore().selectedInstanceId, {
        path: '/api/cameras/' + cameraId + '/still',
        params,
        responseType: 'blob',
        priority,
        signal,
        isPathBound: true,
      })
      if (frameResponse.ok) {
        const frameTimestamp = Date.parse(frameResponse.response.headers.get('X-Still-Frame-Source'))
        if (!this.currentStillFrames[cameraId]) this.currentStillFrames[cameraId] = {}
        this.currentStillFrames[cameraId][resizeY] = {
          frame: URL.createObjectURL(frameResponse.data),
          timestamp: frameTimestamp,
        }
      }
      this.blockedThumbnailLoadings[cameraId][resizeY] = false
    },

    async loadFrameForTimestamp(
      cameraId,
      timestamp,
      resizeY = 1080,
      exact = false,
      maximumTimeDifference = 1000 * 60 * 5,
      signal = undefined,
      higherPriority = false
    ) {
      if (signal?.aborted) return null
      if (useSettingsStore().useAlignmentHelperAsImageReplacement)
        return 'https://storage.googleapis.com/vetvise.appspot.com/public_assets/Alignment-Helper.png'
      const frameResponse = await backendHandler.get(useInstanceStore().selectedInstanceId, {
        path: '/api/recordings/frame/' + cameraId + '/' + timestamp,
        responseType: 'blob',
        params: {
          resizeY: resizeY,
          onlyExactFrame: exact,
          maximumDifference: maximumTimeDifference,
        },
        priority: higherPriority ? 4 : undefined,
        signal,
        isPathBound: false,
      })
      if (frameResponse.ok) return URL.createObjectURL(frameResponse.data)
      useLogsStore().addLogEntry({
        message: `Failed to load frame for camera ${cameraId} at timestamp ${timestamp}`,
        tag: 'cameras',
        globalUniqueIdentifier: useInstanceStore().selectedInstanceId,
        level: 'ERROR',
        error: frameResponse.error,
        httpMethod: 'GET',
      })
      return null
    },

    async startLiveStream(cameraId) {
      const liveStreamResponse = await backendHandler.get(useInstanceStore().selectedInstanceId, { path: '/api/streaming/' + cameraId })
      if (!liveStreamResponse.ok) return null
      return liveStreamResponse.data.path
    },

    async hasStreamAlreadyStarted(streamPath) {
      const response = await backendHandler.get(useInstanceStore().selectedInstanceId, { path: streamPath + 'camera.m3u8' })
      return response.ok
    },

    async setPrivacyForMultipleCameras(cameraIds, privacy) {
      const response = await backendHandler.put(useInstanceStore().selectedInstanceId, {
        path: '/api/privacy/multiple/' + privacy,
        data: { cameras: cameraIds },
      })
      return response.ok
    },

    async loadCameras(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const cameraResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/cameras/query',
        body: {},
        priority: 3,
      })
      if (!cameraResponse.ok) return null
      this.cameras[globalUniqueIdentifier] = cameraResponse.data
      return cameraResponse.data
    },
    async loadCamera(cameraId, signal, globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      if (signal?.aborted) return null
      const cameraResponse = await backendHandler.get(useInstanceStore().selectedInstanceId, { path: '/api/cameras/' + cameraId, signal })
      if (signal?.aborted) return null
      if (!cameraResponse.ok) return null
      if (!this.cameras[globalUniqueIdentifier]) this.cameras[globalUniqueIdentifier] = []
      const cameraIndex = this.cameras[globalUniqueIdentifier].findIndex(c => c.cameraId === cameraId)
      if (cameraIndex !== -1) {
        this.cameras[globalUniqueIdentifier][cameraIndex] = cameraResponse.data
      } else {
        this.cameras[globalUniqueIdentifier].push(cameraResponse.data)
      }
      return cameraResponse.data
    },

    /**
     * Checks if the camera with the given cameraId is online.
     * The camera is considered offline if it has not been seen online for 10 minutes.
     *
     * @param {string} cameraId - The camera id to check.
     * @returns {boolean} - Returns true if the camera is online, otherwise false.
     */
    isOnlineById(cameraId) {
      const cameras = this.getNonDecommissionedCameras()
      const camera = cameras.find(camera => camera.cameraId === cameraId)
      if (!camera) return false
      return camera.lastSeenOnline > Date.now() - 1000 * 60 * 10 // 10 minutes
    },

    /**
     * Checks if the camera is online.
     * The camera is considered offline if it has not been seen online for 10 minutes.
     *
     * @param {object} camera - The camera to check.
     * @returns {boolean} - Returns true if the camera is online, otherwise false.
     */
    isOnline(camera) {
      if (!camera) return false
      return camera.lastSeenOnline > Date.now() - 1000 * 60 * 10 // 10 minutes
    },

    getCameraStatusById(cameraId) {
      return this.isOnlineById(cameraId) ? 'online' : 'offline'
    },

    getCameraStatus(camera) {
      return this.isOnline(camera) ? 'online' : 'offline'
    },

    async rebootAllCameras(globalUniqueIdentifier = useInstanceStore().selectedInstanceId, returnError = false) {
      const allCameras = await this.loadCameras(globalUniqueIdentifier)
      if (!allCameras) {
        if (!returnError) return false
        else return ['Could not load cameras']
      }
      const rebootPromises = allCameras.map(
        camera =>
          new Promise(resolve => {
            this.rebootCamera(camera.cameraId, returnError).then(result => {
              resolve({ cameraId: camera.cameraId, cameraName: camera.name, result: result })
            })
          })
      )
      const results = await Promise.all(rebootPromises)
      if (!returnError) {
        return results.every(r => r.result === true)
      }
      return results
    },
    async rebootCamera(cameraId, returnError = false) {
      const response = await backendHandler.put(useInstanceStore().selectedInstanceId, {
        path: '/api/cameras/reboot/' + cameraId,
      })
      if (!response.ok && returnError) return { error: response.error, data: response.data }
      return response.ok
    },
    async renameCamera(cameraId, newName) {
      const response = await backendHandler.patch(useInstanceStore().selectedInstanceId, {
        path: '/api/cameras/basic/' + cameraId,
        data: { cameraRecord: { name: newName } },
      })
      if (!response.ok) return false
      await this.loadCamera(cameraId)
      return true
    },

    async factoryResetCamera(cameraId) {
      const response = await backendHandler.post(undefined, {
        path: '/api/cameras/factoryReset/' + cameraId,
        isPathBound: false,
      })
      return response.ok
    },

    async ensureCamerasLoaded(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      await SyncableHandler.ensureSyncable(CamerasSyncable, globalUniqueIdentifier)
    },

    keepCamerasLoaded(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      return SyncableHandler.addSyncable(CamerasSyncable, globalUniqueIdentifier)
    },
  },
})
